import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { ResponsiveService } from './shared/responsive.service';
import { ScreenSize, DeviceInfo, YearFile, YearFileSection, TopCalc, YearFileMaster, Client, AddressTemplate, Estimate, ErrorModel, Inbox, CurrentUser, LoginResponse, TempUploadVm, Enquiry, HandiTaxVm, GstCase, GstEstimate } from './models';
import { ToastrService } from 'ngx-toastr';
import { DeviceDetectorService } from 'ngx-device-detector';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Location } from '@angular/common';
import { AppConfig } from './shared/app.config';
import { Subject, Observable } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import * as forge from 'node-forge';
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent } from './views-inner/shared/dialogs/info-dialog/info-dialog.component';
import { ViewDialogComponent } from './views-inner/shared/dialogs/view-dialog/view-dialog.component';
import { CalcDialogComponent } from './views-inner/shared/dialogs/calc-dialog/calc-dialog.component';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';





@Injectable({
  providedIn: 'root',
})
export class GenericService {
  localKey = AppConfig.settings.LocalKey;
  size = new ScreenSize();
  device = new DeviceInfo();
  jwtHelper: JwtHelperService = new JwtHelperService();
  apiUrl = AppConfig.settings.ApiUrl;
  webUrl = AppConfig.settings.WebUrl;
  publicKey = AppConfig.settings.PublicKey.join('\n');
  estimate: Estimate;
  gstEstimate: GstEstimate;
  client: Client;
  handiTaxVm: HandiTaxVm;
  params: any;
  clientTid: string;
  inbox: Inbox;
  topCalc: TopCalc;
  topCalcSubject = new Subject<TopCalc>();
  yearFile: YearFile;
  yearFileSection: YearFileSection;
  rsa = forge.pki.publicKeyFromPem(this.publicKey);
  dataUri: string;

  private yearFileData: YearFile;
  private yearFileSectionData: YearFileSection;
  private clientData: Client;

  constructor(private http: HttpClient, private location: Location, public router: Router, public responsiveService: ResponsiveService, private toastr: ToastrService, private deviceService: DeviceDetectorService, public dialog: MatDialog) {
    this.getScreenInfo();
    this.getDeviceInfo();
  }

  // Local Storage Helpers ========================================================================================================

  slog(obj) {
    if (environment.production === false) {
      console.log(JSON.stringify(obj));
    }
  };

  olog(obj) {
    if (environment.production === false) {
      console.log(obj);
    }
  };

  getUser(): CurrentUser {
    const aToken = this.getParams('cToken') as LoginResponse;
    const user = new CurrentUser();
    const payload = this.jwtHelper.decodeToken(aToken.token);
    user.UserId = payload?.UserId;
    user.Email = payload?.Email;
    user.UserName = payload?.UserName;
    user.Name = payload?.Name;
    user.Fname = payload?.Fname;
    user.UserTypeId = payload?.UserTypeId;
    user.Role = payload?.Role;
    user.AgentId = payload?.AgentId;
    user.ClientId = payload?.ClientId;
    user.EnquiryId = payload?.EnquiryId;
    user.TeamId = payload?.TeamId;
    user.IsPartOfTeam = payload?.IsPartOfTeam;
    user.TeamName = payload?.TeamName;
    user.IsLeader = payload?.IsLeader;
    user.YourCode = payload?.YourCode;
    return user;
  }

  setParams(key: string, value: any) {
    localStorage.setItem(key, this.encryptLocal(this.localKey, value));
  }

  prepareTempUploadVm(file: File, idx: number): TempUploadVm {
    this.slog('idx: ' + idx);
    const item = new TempUploadVm();
    item.id = 0;
    item.idx = idx;
    item.size = this.getFileSize(file.size);
    item.ext = this.getFileExtension(file.name);
    item.name = file.name;
    item.progress = 0;
    item.contentType = this.getContentType(file.name);
    return item;
  }

  arrayRemove(arr, value) {
    return arr.filter(function (ele) {
      return ele != value;
    });
  }

  getFileExtension(filename): string {
    return '.' + (/[.]/.exec(filename)) ? /[^.]+$/.exec(filename)[0] : undefined;
    // return '.' + filename.split('.').pop();
  }

  getFileSize(fileSizeInBytes: number): string {
    var i = -1;
    var byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
    do {
      fileSizeInBytes = fileSizeInBytes / 1024;
      i++;
    } while (fileSizeInBytes > 1024);

    return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
  }

  getContentType(fileName: string): string {
    // return mime.lookup(fileName);
    return '';
  }


  getFormValue(field): any {
    if (field !== null) {
      return field.value;
    } else {
      return null;
    }
  }

  getParams(key: string): any {
    const value = localStorage.getItem(key);
    if (value === null) {
      return '';
    }
    const decrypted = this.decryptLocal(this.localKey, value.toString());
    const result = JSON.parse(JSON.stringify(decrypted));
    return JSON.parse(result);
  }

  removeItem(key: string) {
    localStorage.removeItem(key);
  }

  // Using Local Key
  encryptLocal(keys: string, value: string) {
    const key = CryptoJS.enc.Utf8.parse(keys);
    const iv = CryptoJS.enc.Utf8.parse(keys);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(value), key,
      {
        keySize: 16,
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });
    return encrypted.toString();
  }

  // Using Local Key
  decryptLocal(keys: string, value: string) {
    const key = CryptoJS.enc.Utf8.parse(keys);
    const iv = CryptoJS.enc.Utf8.parse(keys);
    const decrypted = CryptoJS.AES.decrypt(value, key, {
      keySize: 16,
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8).replace(/^"|"$/g, '');
  }

  // Using Public Key
  encrypt(value: any) {
    return window.btoa(this.rsa.encrypt(value));
  }

  getToken(): string {
    const aToken = this.getParams('cToken') as LoginResponse;
    return aToken.token;
  }

  hasAccess(val: number): boolean {
    const aToken = this.getParams('cToken') as LoginResponse;
    return aToken.accessIds.includes(val);
  }

  isAuthenticated(): boolean {
    const aToken = this.getParams('cToken') as LoginResponse;
    return !this.jwtHelper.isTokenExpired(aToken.token);
  }

   // Get Value From Form

//   getFormValue(field): any {
//     if (field !== null) {
//         return field.value;
//     } else {
//         return null;
//     }
// }

getDateStr(dateStr): string {
    return moment(dateStr).format('DD/MM/yyyy');
}


getRapidDateStr(dateStr): string {
    return moment(dateStr).format('YYYY-MM-DD');
}

getRapidDateStrForGreenMedicare(dateStr): string {
    return moment(dateStr).format('yyyy-MM');
}

  // Http Return Result Services ===============================================================================================

  delParams(key: string) {
    return localStorage.removeItem(key);
  }

  delAllParams() {
    return localStorage.clear();
  }

  setYearFileData(data: YearFile): any {
    this.yearFileData = data;
  }

  getYearFileData(): any {
    return this.yearFileData;
  }

  setYearFileSectionData(data: YearFileSection): any {
    this.yearFileSectionData = data;
  }

  getYearFileSectionData(): YearFileSection {
    return this.yearFileSectionData;
  }

  setClientData(data: Client): any {
    this.clientData = data;
  }

  getClientData(): any {
    return this.clientData;
  }

  updateCalc(id: number): any {
    this.getYearFileCalcById(id).then(response => {
      this.slog(44444);
      this.topCalcSubject.next(response);
    });
  }

  clearCalc(): void {
    this.topCalcSubject.next();
  }

  getCalc(): Observable<TopCalc> {
    return this.topCalcSubject.asObservable();
  }


  getCalcForFinish(id: number): any {
    this.getYearFileCalcById(id).then(response => {
      this.topCalc = response;
    });
  }



  getDeviceInfo() {
    this.device.browser = this.deviceService.browser;
    this.device.browserVersion = this.deviceService.browser_version;
    this.device.os = this.deviceService.os;
    this.device.osVersion = this.deviceService.os_version;
    this.device.isDesktop = this.deviceService.isDesktop();
    this.device.isMobile = this.deviceService.isMobile();
    this.device.isTablet = this.deviceService.isTablet();
  }

  getScreenInfo() {
    this.size.xs = this.responsiveService.xs();
    this.size.sm = this.responsiveService.sm();
    this.size.md = this.responsiveService.md();
    this.size.lg = this.responsiveService.lg();
    this.size.xl = this.responsiveService.xl();
    this.size.small = this.responsiveService.small();
  }

  logout(): void {
    console.log('logout called');
    localStorage.removeItem('cToken');
    localStorage.removeItem('cFullName');
    localStorage.removeItem('cFname');
    this.location.replaceState('/');
    this.delAllParams();
    this.router.navigate(['/auth/login'], { replaceUrl: true });
  }



  error1(error: ErrorModel, angularLocation = '', hasTitle = false, title = 'Failed'): any {
    console.log('Angular: ' + angularLocation);
    console.log('Controller: ' + error.controller);
    console.log('Action: ' + error.action);
    console.log(error.error);
    if (hasTitle) {
      if((error.error + '') === 'Link has been expired!!!'){
        this.router.navigate(['/auth/login'], { replaceUrl: true });
      }
      return this.toastr.error(error.error + '', title, { positionClass: 'toast-bottom-center' });
    } else {
      if((error.error + '') === 'Link has been expired!!!'){
        this.router.navigate(['/auth/login'], { replaceUrl: true });
      }
      return this.toastr.error(error.error + '', '', { positionClass: 'toast-bottom-center' });
    }
  }

  error(error: string, hasTitle = false, title = 'Failed', position = 'toast-bottom-right'): any {
    if (hasTitle) {
      return this.toastr.error(error, title, { positionClass: position });
    } else {
      return this.toastr.error(error, '', { positionClass: position });
    }
  }

  success(msg: string, hasTitle = false, title = 'Success', position = 'toast-bottom-right'): any {
    if (hasTitle) {
      return this.toastr.success(msg, title, { positionClass: position });
    } else {
      return this.toastr.success(msg, '', { positionClass: position });
    }
  }

  warn(msg: string, hasTitle = false, title = 'Success', position = 'toast-bottom-right'): any {
    if (hasTitle) {
      return this.toastr.warning(msg, title, { positionClass: position });
    } else {
      return this.toastr.warning(msg, '', { positionClass: position });
    }
  }


  isValChanged(formVal: any, modelVal: any): boolean {
    // console.log('formVal: ' + formVal);
    // console.log('modelVal: ' + modelVal);
    if ((formVal === modelVal) || (formVal === '' && modelVal === null)) {
      return false;
    } else {
      return true;
    }
  }

  infoDialog(name: string, title: any): void {
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      data: { name: name, title: title }
    });

    dialogRef.afterClosed().subscribe(result => { });
  }

  searchAddress(value: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post<AddressTemplate[]>('https://mappify.io/api/rpc/address/autocomplete/', { streetAddress: value, apiKey: '42c85d24-734a-47b9-a274-51a2692f672e', formatCase: true }).subscribe((response: any) => {
        resolve(response);
      }, reject);
    });
  }

  // getTokenPayload(): any {
  //   const token = localStorage.getItem('cToken');
  //   this.tokenPayload = this.jwtHelper.decodeToken(token);
  //   return this.tokenPayload;
  // }

  getSetting(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'generic/GetTaxSetting/').subscribe((response: any) => {
        resolve(response);
      }, reject);
    });
  }

  viewDialog(gkey: string, obj: any): void {
    const dialogRef = this.dialog.open(ViewDialogComponent, {
      // width: '250px',
      data: { gkey: gkey, obj }
    });

    dialogRef.afterClosed().subscribe(result => { });
  }

  log(data: any): void {
    console.log('From Generic Service: ' + JSON.stringify(data));
  }

  calcDialog(topCalc: TopCalc): void {
    const dialogRef = this.dialog.open(CalcDialogComponent, {
      width: '1200px',
      data: { topCalc: topCalc }
    });

    dialogRef.afterClosed().subscribe(result => { });
  }



  // Base Services ===========================================================================================================
  getDataByModel(path: string, model: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + path, { params: model }).subscribe((response: any) => {
        resolve(response);
      }, reject);
    });
  }

  getData(path: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + path).subscribe((response: any) => {
        resolve(response);
      }, reject);
    });
  }

  postDataByModel(path: string, model: object): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(this.apiUrl + path, model).subscribe((response: any) => {
        resolve(response);
      }, reject);
    });
  }

  postId(path: string, id: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(this.apiUrl + path, id).subscribe((response: any) => {
        resolve(response);
      }, reject);
    });
  }

  // Resolve Services ===========================================================================================================
  getYearFileCalcById(id: number): Promise<TopCalc> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'invoiceYear/GetInvoiceYearCalcById/' + id).subscribe((response: any) => {
        this.topCalc = response;
        resolve(response);
      }, reject);
    });
  }


  getYearFileById(id: string): Promise<YearFileMaster> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'invoiceYear/GetInvoiceYearById/' + id).subscribe((response: any) => {
        this.yearFile = response.yearFile as YearFile;
        this.topCalc = response.topCalc;
        this.yearFileSection = response.section as YearFileSection;
        this.topCalcSubject.next(this.topCalc);
        resolve(response as YearFileMaster);
      }, reject);
    });
  }

  getClientEstimateById(id: string): Promise<Estimate> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Authority/GetInvoiceForAuthority/' + id).subscribe((response: any) => {
        if (response.success) {
          this.estimate = response.dto as Estimate;
          resolve(response as Estimate);
        } else {
          this.error1(response, 'GenericService-280');
        }
      }, reject);
    });
  }

  getProfile(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'client/GetProfile').subscribe((response: any) => {
        this.client = response as Client;
        resolve(response as Client);
      }, reject);
    });
  }

  getAuthorityById(invGuid: string): Promise<Estimate> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Authority/GetInvoiceForAuthority/' + invGuid).subscribe((response: any) => {
        if (response.success) {
          this.estimate = response.dto as Estimate;
          resolve(response as Estimate);
        } else {
          this.error1(response, 'GenericService-293');
        }
      }, reject);
    });
  }

  getClientIdProofById(invGuid: string): Promise<Estimate> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Authority/GetClientIdProofByGid/' + invGuid).subscribe((response: any) => {
        console.log(JSON.stringify(response.dto));
        if (response.success) {
          this.estimate = response.dto as Estimate;
          resolve(response as Estimate);
        } else {
          this.error1(response, 'GenericService-306');
        }
      }, reject);
    });
  }

  
  getGstAuthorityById(gid: string): Promise<GstEstimate> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Authority/GetGstForAuthority/' + gid).subscribe((response: any) => {
        if (response.success) {
          this.gstEstimate = response.dto as GstEstimate;
          resolve(response as GstEstimate);
        } else {
          this.error1(response, 'GenericService-293');
        }
      }, reject);
    });
  }

  getHandiTaxEnquiryById(tid: string): Promise<HandiTaxVm> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Enquiry/GetHandiTaxEnquiryById/' + tid).subscribe((response: any) => {
        if (response.success) {
          this.handiTaxVm = response.dto as HandiTaxVm;
          resolve(response as HandiTaxVm);
        } else {
          this.error1(response, 'GenericService-486');
        }
      }, reject);
    });
  }

  


  getInboxById(id): Promise<Inbox> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'inbox/GetInboxByIdForClient/' + id).subscribe((response: any) => {
        if (response.success) {
          this.inbox = response.dto as Inbox;
          resolve(response as Inbox);
        } else {
          this.error1(response, 'GenericService-306');
        }
      }, reject);
    });
  }

  getEnquiryUnsub(gid: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Authority/UnsubscribeFromEnquiry/' + gid).subscribe((response: any) => {
        if (response.success) {
          resolve(true);
        } else {
          this.error1(response, 'GenericService-293');
        }
      }, reject);
    });
  }

  getInvoiceUnsub(gid: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.get(this.apiUrl + 'Authority/UnsubscribeFromInvoice/' + gid).subscribe((response: any) => {
        if (response.success) {
          resolve(true);
        } else {
          this.error1(response, 'GenericService-293');
        }
      }, reject);
    });
  }

}


// Resolvers Services ========================================================================================================
@Injectable()
export class YearFileResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const eid = this.service.getParams('yearfileid');
    console.log('eid ' + eid);
    return this.service.getYearFileById(eid);
  }
}


@Injectable()
export class EstimateResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('id');
    return this.service.getClientEstimateById(id);
  }
}

@Injectable()
export class ProfileResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    return this.service.getProfile();
  }
}

@Injectable()
export class AuthorityResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('gid');
    return this.service.getAuthorityById(id);
  }
}


@Injectable()
export class GstAuthorityResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('gid');
    return this.service.getGstAuthorityById(id);
  }
}

@Injectable()
export class ClientIdProofResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('gid');
    return this.service.getClientIdProofById(id);
  }
}

@Injectable()
export class MessageResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('id');
    return this.service.getInboxById(id);
  }
}

@Injectable()
export class HandiTaxResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('tid');
    return this.service.getHandiTaxEnquiryById(id);
  }
}


@Injectable()
export class EnquiryUnsubResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('gid');
    return this.service.getEnquiryUnsub(id);
  }
}

@Injectable()
export class InvoiceUnsubResolve implements Resolve<any> {
  constructor(private service: GenericService) { }
  resolve(route: ActivatedRouteSnapshot): any {
    const id = route.paramMap.get('gid');
    return this.service.getInvoiceUnsub(id);
  }
}



